/* ============================================================================
   AKINOD — En-tête
   ----------------------------------------------------------------------------
   Le méga-menu s'ouvre AU SURVOL, entièrement en CSS (voir la feuille de style
   de la section). Ce script n'y touche pas en desktop : c'est délibéré.

   Historique des versions écartées, pour qu'on n'y revienne pas :
   · ouverture en JavaScript avec une minuterie de fermeture de 180 ms — le menu
     se fermait quand on descendait vite, ou restait ouvert après un aller-retour ;
   · ouverture au clic — un menu qui exige un clic là où le survol suffit est un
     recul d'ergonomie en desktop ;
   · rustines CSS (`:has()`, pont invisible) autour d'un panneau posé à côté de
     son bouton — elles traitaient le symptôme, pas la cause.

   Le défaut était structurel : le panneau était un frère du bouton, si bien que
   descendre de l'un vers l'autre faisait sortir le curseur de la zone survolée.
   Le panneau est désormais un enfant du déclencheur, qui occupe toute la hauteur
   de la barre : le survol est continu par construction. Le JavaScript ne garde
   donc que ce que le survol ne couvre pas : le tactile (où le survol n'existe
   pas), la touche Échap et le tiroir mobile.

   Idempotent, et rebranché sur `shopify:section:load` : l'éditeur de thème
   réinjecte l'en-tête sans recharger la page, et sans cela le burger, Échap et
   la pastille restaient muets jusqu'au rechargement suivant.
   ========================================================================== */

(() => {
  'use strict';

  /* --- La pastille du panier ------------------------------------------------
     Elle est rendue par Liquid au chargement, et ne bougeait ensuite que depuis
     les deux fiches produit, qui la recalculaient chacune de leur côté. Tout
     changement venu de la page panier — quantité, corbeille, case d'emballage
     cadeau — la laissait donc sur son compte d'avant : quatre articles dans le
     panier, deux sur la pastille. Constaté le 07/09.

     Les fiches ne comptent plus : elles appellent `AkinodPanier.recompter`,
     exporté plus bas. Et deux écouteurs — lignes et remises — couvrent les
     changements émis par Shopify, d'où qu'ils viennent. Le compte définitif
     arrive avec la promesse jointe à l'évènement, une fois le serveur ayant
     répondu.

     Le nom des évènements est écrit en toutes lettres : ce fichier est un script
     classique, pas un module, il ne peut pas importer `StandardEvents`. */

  /* Le compte n'est plus celui de Shopify : la maquette compte les étuis offerts et
     pas la pochette cadeau. Il se calcule donc là où il se calcule déjà pour la page
     du panier, dans le gabarit `akinod-compte-articles`, que l'on se fait rendre à
     chaque changement plutôt que de le réécrire ici en JavaScript.

     La pastille est relue à chaque fois, et non capturée une fois pour toutes :
     l'éditeur de thème remplace le balisage de l'en-tête, et une référence
     gardée piloterait un nœud détaché de la page. */
  const pastille = () => document.querySelector('[data-ak-cart-count]');

  const poser = (n) => {
    const cible = pastille();
    if (!cible || typeof n !== 'number' || Number.isNaN(n)) return;

    cible.textContent = String(n);
    cible.classList.toggle('is-empty', n === 0);
  };

  let recompte = null;

  const recompter = () => {
    recompte ??= (async () => {
      try {
        const racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const rendu = await fetch(`${racine}?sections=akinod-compte-articles`).then((r) => r.json());
        const html = new DOMParser().parseFromString(rendu['akinod-compte-articles'] || '', 'text/html');

        poser(Number.parseInt(html.querySelector('[data-ak-articles]')?.textContent ?? '', 10));
      } catch {
        // La pastille se recalculera au prochain chargement de page.
      } finally {
        recompte = null;
      }
    })();

    return recompte;
  };

  window.AkinodPanier = Object.assign(window.AkinodPanier || {}, { recompter });

  /* Posés une seule fois, hors de `init` : ils ne visent aucun nœud, et l'éditeur
     de thème n'a donc pas à les renouveler. */
  const apres = (e) => {
    if (!pastille()) return;
    e.promise?.then(recompter).catch(() => {
      // Un changement refusé n'a rien modifié : la pastille reste juste.
    });
  };

  document.addEventListener('shopify:cart:lines-update', apres);
  document.addEventListener('shopify:cart:discount-update', apres);

  /* --- Méga-menu et tiroir --------------------------------------------------
     Eux visent des nœuds, et l'éditeur les remplace. On coupe d'un seul geste
     tous les écouteurs du rendu précédent plutôt que de les laisser s'empiler
     sur des éléments détachés. */

  let abandon = null;

  function init() {
    const nav = document.querySelector('.ak-nav');
    if (!nav || nav.dataset.akHeaderBound) return;
    nav.dataset.akHeaderBound = '1';

    abandon?.abort();
    abandon = new AbortController();
    const { signal } = abandon;

    /* --- Méga-menu : tactile et clavier uniquement --------------------------- */

    const item = nav.querySelector('.ak-nav__item--mega');
    const trigger = item?.querySelector('.ak-nav__link');
    const mega = nav.querySelector('#ak-mega');

    if (item && trigger && mega) {
      // Un appareil sans survol : le clic devient l'unique moyen d'ouvrir.
      // En desktop on ne touche à rien, sous peine de figer un état que le CSS
      // gère déjà correctement.
      const coarse = window.matchMedia('(hover: none)');

      const isOpen = () => trigger.getAttribute('aria-expanded') === 'true';
      const setOpen = (open) => trigger.setAttribute('aria-expanded', String(open));

      /* L'état de fermeture posé par Échap (voir plus bas). Il ne doit durer que
         le temps de quitter le déclencheur, sinon un menu fermé au clavier le
         resterait pour de bon : rien d'autre ne viendrait lever la classe. */
      const rouvrir = () => item.classList.remove('is-ferme');

      trigger.addEventListener('click', (e) => {
        rouvrir();                      // ré-activer le déclencheur rouvre
        if (!coarse.matches) return;    // desktop : le survol s'en charge
        e.preventDefault();
        setOpen(!isOpen());
      }, { signal });

      document.addEventListener('click', (e) => {
        if (coarse.matches && isOpen() && !nav.contains(e.target)) setOpen(false);
      }, { signal });

      /* Échap ferme, y compris une ouverture obtenue au clavier.

         Rendre le focus au déclencheur ne suffisait pas : il est un DESCENDANT
         de `.ak-nav__item--mega`, si bien que le sélecteur d'ouverture du CSS
         continuait de matcher et que le panneau restait ouvert. D'où une classe
         de fermeture explicite, que la feuille de style lit. */
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const dedans = item.contains(document.activeElement);
        if (isOpen()) setOpen(false);
        if (mega.contains(document.activeElement)) trigger.focus();
        if (dedans) item.classList.add('is-ferme');
      }, { signal });

      item.addEventListener('mouseleave', rouvrir, { signal });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) rouvrir();
      }, { signal });

      // Repasser en desktop ne doit pas laisser un état tactile verrouillé.
      coarse.addEventListener('change', (e) => { if (!e.matches) setOpen(false); }, { signal });
    }

    /* --- Tiroir mobile ------------------------------------------------------- */

    const drawer = document.getElementById('ak-drawer');
    const openBtn = nav.querySelector('[data-ak-drawer-open]');

    if (drawer && openBtn) {
      let lastFocused = null;
      let masquer = null;   // l'écouteur de fin de transition en attente, s'il y en a un

      const oublierTransition = () => {
        if (!masquer) return;
        drawer.removeEventListener('transitionend', masquer);
        masquer = null;
      };

      const openDrawer = () => {
        // Un écouteur de fermeture encore en vie refermerait ce tiroir-là.
        oublierTransition();
        lastFocused = document.activeElement;
        drawer.hidden = false;
        // Un frame de décalage, sinon la transition ne joue pas depuis `hidden`.
        requestAnimationFrame(() => drawer.classList.add('is-open'));
        openBtn.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        drawer.querySelector('[data-ak-drawer-close]')?.focus();
      };

      const closeDrawer = () => {
        drawer.classList.remove('is-open');
        openBtn.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';

        /* Pas de transition, pas de `transitionend`. Au-dessus de 1081 px le
           tiroir est en `display: none` : la fermeture déclenchée par la bascule
           de largeur laissait `hidden` à `false` — un tiroir hors écran mais
           toujours atteignable au clavier — ET un écouteur en vie, qui refermait
           le tiroir à la réouverture suivante en laissant `body` bloqué. On
           masque donc immédiatement dans ce cas.

           La cible est vérifiée : les transitions des enfants du tiroir
           remontent jusqu'ici et masqueraient le tiroir avant la fin de la
           sienne. */
        oublierTransition();
        if (getComputedStyle(drawer).display === 'none') {
          drawer.hidden = true;
        } else {
          masquer = (e) => {
            if (e.target !== drawer) return;
            oublierTransition();
            drawer.hidden = true;
          };
          drawer.addEventListener('transitionend', masquer, { signal });
        }

        lastFocused?.focus();
      };

      /* On quitte la page par un lien du tiroir (CAT-002 en a ajouté quatre) :
         le tiroir se referme en partant. Sinon, au bouton précédent, Safari et
         Chrome restaurent la page depuis leur cache telle qu'on l'a laissée,
         tiroir ouvert par-dessus et défilement bloqué (`overflow: hidden`).
         Vu au simulateur iPhone le 22/09. Sans transition : la page part déjà.

         Aucun écouteur sur les liens de rubrique eux-mêmes : un clic sur un
         lien posé dans un `summary` suit le lien sans déplier, le navigateur
         ne confiant le dépliage au `summary` que si rien de plus proche n'a
         d'action propre. Le `stopPropagation` posé ici le 21/09 ne changeait
         rien à ça, et cachait ces clics aux autres écouteurs, dont GTM. */
      window.addEventListener('pagehide', () => {
        if (drawer.hidden) return;
        oublierTransition();
        drawer.classList.remove('is-open');
        drawer.hidden = true;
        openBtn.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      }, { signal });

      openBtn.addEventListener('click', openDrawer, { signal });
      drawer.querySelector('[data-ak-drawer-close]')?.addEventListener('click', closeDrawer, { signal });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !drawer.hidden) closeDrawer();
      }, { signal });

      // Repasser en desktop avec le tiroir ouvert laisserait le défilement bloqué.
      window.matchMedia('(min-width: 1081px)').addEventListener('change', (e) => {
        if (e.matches && !drawer.hidden) closeDrawer();
      }, { signal });
    }

    /* --- Langue ----------------------------------------------------------------
       La liste envoyait son formulaire à chaque `change`. Or sous Windows, une
       flèche sur une liste FERMÉE change sa valeur et émet `change` à chaque cran :
       la page se rechargeait au premier appui, et l'allemand ne s'atteignait qu'en
       passant par l'anglais (WCAG 3.2.2, « À la saisie »).

       Un `change` qui suit une touche dans la foulée — même tâche, quelques
       millisecondes — vient donc d'une flèche : on parcourt, on n'envoie pas, et
       Entrée valide. Tout autre `change` est un choix rendu par la liste
       déroulante du système — clic, doigt, ou Entrée dans la liste ouverte — et
       part aussitôt, comme avant. Quitter la liste sans valider la remet sur la
       langue de la page : c'est celle qu'affiche la barre.

       Une langue servie sur un autre domaine (`data-ak-ailleurs`) ne passe pas par le
       formulaire, qui ne connaît que les langues d'ici : on emmène le visiteur sur la
       même page dans cette langue, dont Shopify donne l'adresse dans les balises
       hreflang de l'en-tête, et à défaut sur l'accueil de ce domaine. */
    const memePage = (code) => document.querySelector(
      `link[rel="alternate"][hreflang="${code}"], link[rel="alternate"][hreflang^="${code}-"]`
    )?.href;

    document.querySelectorAll('.ak-langues select').forEach((liste) => {
      const courante = liste.value;
      let toucheA = -Infinity;
      const envoyer = () => {
        if (liste.value === courante) return;
        const ailleurs = liste.selectedOptions[0]?.dataset.akAilleurs;
        if (ailleurs) window.location.href = memePage(liste.value) || ailleurs;
        else liste.form?.submit();
      };

      liste.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && liste.value !== courante) {
          e.preventDefault();
          envoyer();
          return;
        }
        toucheA = performance.now();
      }, { signal });

      liste.addEventListener('change', () => {
        if (performance.now() - toucheA < 100) return;
        envoyer();
      }, { signal });

      liste.addEventListener('blur', () => { liste.value = courante; }, { signal });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // L'éditeur de thème réinjecte les sections sans recharger la page.
  document.addEventListener('shopify:section:load', () => init());
})();
